import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenantSchema,
  LICENSE_STATUS_LABELS,
  LICENSE_STATUSES,
  MODULE_LABELS,
  MODULE_REQUIRES,
  MODULES,
  PLAN_DEFAULT_MODULES,
  PLAN_DESCRIPTIONS,
  PLAN_INCLUDED_MODULES,
  PLAN_LABELS,
  planDisplayName,
  PLANS,
  PROVINCES,
  COUNTRIES,
  type SellerDTO,
  type CreateTenantInput,
  type CreateTenantResponse,
  type LicenseStatus,
  type ModuleKey,
  type Plan,
  type TenantRowDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
  type Tone,
} from '@bifeai/ui';
import { Copy, KeyRound, Plus, Search, Store, Archive, Database } from 'lucide-react';
import { useState } from 'react';
import type { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { api, applyFieldErrors, errorMessage } from '../lib/api';
import { ManualClients } from './ManualClients';
import { useCan } from '../lib/session';
import { formatDate } from '../lib/format';

const LICENSE_TONE: Record<LicenseStatus, Tone> = {
  ACTIVE: 'success',
  GRACE: 'warning',
  READ_ONLY: 'warning',
  SUSPENDED: 'danger',
};

export function TenantsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'all' | 'manual'>(() => (new URLSearchParams(window.location.search).get('vista') === 'manual' ? 'manual' : 'all'));
  const [creating, setCreating] = useState(false);
  const can = useCan();
  const [editing, setEditing] = useState<TenantRowDTO | null>(null);
  const [secret, setSecret] = useState<{
    title: string;
    email: string;
    password: string;
  } | null>(null);
  const tenants = useQuery({
    queryKey: ['tenants', search],
    queryFn: () => api.get<TenantRowDTO[]>(`/platform/tenants${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });

  return (
    <>
      <PageHeader
        title="Clientes"
        subtitle="Carnicerías que usan BifeAI: plan, módulos, licencia y vencimiento del abono."
        actions={
          can('tenants.manage') && (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Nuevo cliente
            </Button>
          )
        }
      />
      <div className="bf-view-switch" role="tablist" aria-label="Vista" style={{ marginBottom: 16 }}>
        <button type="button" role="tab" aria-selected={tab === 'all'} onClick={() => setTab('all')}>
          Todos
        </button>
        <button type="button" role="tab" aria-selected={tab === 'manual'} onClick={() => setTab('manual')}>
          Cobro manual
        </button>
      </div>
      {tab === 'manual' ? (
        <ManualClients />
      ) : (
        <>
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: 12,
                top: 13,
                color: 'var(--bf-faint)',
              }}
              aria-hidden
            />
            <Input
              aria-label="Buscar clientes"
              placeholder="Buscar por nombre, razón social o CUIT"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 36 }}
            />
          </div>
          <Card>
            <CardBody flush>
              {tenants.isPending && (
                <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
                  <Skeleton />
                  <Skeleton />
                </div>
              )}
              {tenants.data?.length === 0 && (
                <EmptyState
                  icon={<Store />}
                  title={search ? 'Sin resultados' : 'Todavía no hay clientes'}
                  text={search ? 'Probá con otro nombre o CUIT.' : 'Dá de alta la primera carnicería.'}
                />
              )}
              {tenants.data && tenants.data.length > 0 && (
                <div className="bf-table-wrap">
                  <table className="bf-table">
                    <thead>
                      <tr>
                        <th>Carnicería</th>
                        <th>Plan</th>
                        <th>Módulos</th>
                        <th>Sucursales</th>
                        <th>Usuarios</th>
                        <th>Abono hasta</th>
                        <th>Licencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.data.map((t) => (
                        <tr key={t.id} data-clickable="true" onClick={() => setEditing(t)}>
                          <td>
                            <div className="bf-cell-main">
                              {t.name} {!t.termsVersion && <Badge tone="warning">Sin firmar términos</Badge>}
                              {t.manualBilling && <Badge>Cobro manual</Badge>}
                            </div>
                            <div className="bf-cell-sub">{t.ownerEmail ?? 'Sin dueño cargado'}</div>
                            {(t.province || t.sellerName) && (
                              <div className="bf-cell-sub">
                                {[[t.locality, t.province].filter(Boolean).join(', '), t.sellerName && `Vendió ${t.sellerName}`]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            )}
                          </td>
                          <td>
                            {planDisplayName(t.plan, t.modules)}
                            {t.billingMode === 'SUBSCRIPTION' && (
                              <>
                                {' '}
                                <Badge tone="info">Abono mensual</Badge>
                              </>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {t.modules.length === 0 && <span className="bf-cell-sub">Ninguno</span>}
                              {t.modules.map((m) => (
                                <Badge key={m} tone="info">
                                  {MODULE_LABELS[m]}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="bf-num">{t.branches}</td>
                          <td className="bf-num">{t.users}</td>
                          <td>{t.serviceUntil ? formatDate(t.serviceUntil) : <span className="bf-cell-sub">Sin fecha</span>}</td>
                          <td>
                            <Badge tone={LICENSE_TONE[t.licenseStatus]} dot>
                              {LICENSE_STATUS_LABELS[t.licenseStatus]}
                            </Badge>
                            {t.openErrors > 0 && (
                              <div className="bf-cell-sub" style={{ marginTop: 4, color: 'var(--bf-danger)' }}>
                                {t.openErrors} {t.openErrors === 1 ? 'error abierto' : 'errores abiertos'}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {creating && (
        <CreateTenantDialog
          onClose={() => setCreating(false)}
          onCreated={async (res) => {
            setCreating(false);
            setSecret({
              title: `${res.tenant.name} ya está en BifeAI`,
              email: res.owner.email,
              password: res.owner.temporaryPassword,
            });
            await qc.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
      {editing && (
        <EditTenantDialog
          tenant={editing}
          onClose={() => setEditing(null)}
          onSecret={(email, password) =>
            setSecret({
              title: 'Nueva contraseña temporal del dueño',
              email,
              password,
            })
          }
        />
      )}
      {secret && <SecretDialog {...secret} onClose={() => setSecret(null)} />}
    </>
  );
}

/** Módulos: lo incluido en el plan aparece fijo; el resto son adicionales que se prenden o apagan. */
function ModuleSwitches({ plan, value, onChange }: { plan: Plan; value: ModuleKey[]; onChange: (v: ModuleKey[]) => void }) {
  const included = PLAN_INCLUDED_MODULES[plan];
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {MODULES.map((m) => {
        const isIncluded = included.includes(m);
        // Franquicias va arriba de Multisucursal: sin él, no se puede prender.
        const req = MODULE_REQUIRES[m];
        const missing = !!req && !included.includes(req) && !value.includes(req);
        return (
          <label
            key={m}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span>
              {MODULE_LABELS[m]}
              {isIncluded ? (
                <span className="bf-cell-sub"> · incluido en el plan</span>
              ) : (
                <span className="bf-cell-sub">
                  {' '}
                  · adicional{m === 'ARCA' ? ' (próximamente)' : ''}
                  {req ? ` · requiere ${MODULE_LABELS[req]}` : ''}
                </span>
              )}
            </span>
            <Switch
              label={MODULE_LABELS[m]}
              checked={isIncluded || value.includes(m)}
              disabled={isIncluded || (missing && !value.includes(m))}
              onCheckedChange={(on) =>
                onChange(
                  on
                    ? [...value, m]
                    : // Al apagar Multisucursal se apaga también lo que depende de él.
                      value.filter((x) => x !== m && MODULE_REQUIRES[x] !== m),
                )
              }
            />
          </label>
        );
      })}
    </div>
  );
}

function CreateTenantDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (r: CreateTenantResponse) => Promise<void> }) {
  const [formError, setFormError] = useState<string | null>(null);
  const nextYear = new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10);
  const { register, handleSubmit, control, setValue, setError, formState, watch } = useForm<
    z.input<typeof createTenantSchema>,
    unknown,
    CreateTenantInput
  >({
    resolver: zodResolver(createTenantSchema),
    defaultValues: {
      plan: 'CARNICERIA',
      modules: PLAN_DEFAULT_MODULES.CARNICERIA,
      headquartersName: 'Casa central',
      serviceUntil: nextYear,
    },
  });
  const e = formState.errors;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onCreated(await api.post<CreateTenantResponse>('/platform/tenants', values));
    } catch (err) {
      if (!applyFieldErrors(err, setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => !o && onClose()}
      title="Nuevo cliente"
      description="Se crea la carnicería, su casa central y el usuario del dueño con una contraseña temporal."
    >
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <div className="bf-form-row">
          <Field label="Nombre de la carnicería" error={e.name?.message}>
            <Input autoFocus placeholder="Carnicería Don Bife" {...register('name')} />
          </Field>
          <Field label="Razón social" error={e.legalName?.message}>
            <Input {...register('legalName')} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="CUIT" hint="Opcional" error={e.cuit?.message}>
            <Input placeholder="30-12345678-9" {...register('cuit')} />
          </Field>
          <Field label="Teléfono de contacto" error={e.contactPhone?.message}>
            <Input {...register('contactPhone')} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Plan" error={e.plan?.message} hint={PLAN_DESCRIPTIONS[watch('plan') ?? 'CARNICERIA']}>
            <Select
              {...register('plan', {
                onChange: (ev: { target: { value: string } }) => setValue('modules', PLAN_DEFAULT_MODULES[ev.target.value as Plan]),
              })}
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>
                  {PLAN_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Abono pago hasta" error={e.serviceUntil?.message}>
            <Input type="date" {...register('serviceUntil')} />
          </Field>
        </div>
        <div className="bf-field">
          <span className="bf-label">Módulos</span>
          <Controller
            control={control}
            name="modules"
            render={({ field }) => <ModuleSwitches plan={watch('plan') ?? 'CARNICERIA'} value={field.value ?? []} onChange={field.onChange} />}
          />
        </div>
        <div className="bf-form-row">
          <Field label="Nombre del dueño" error={e.ownerName?.message}>
            <Input {...register('ownerName')} />
          </Field>
          <Field label="Email del dueño" error={e.ownerEmail?.message}>
            <Input type="email" {...register('ownerEmail')} />
          </Field>
        </div>
        <Field label="Notas internas" error={e.notes?.message}>
          <Textarea placeholder="Cómo llegó, condiciones especiales, cuotas acordadas…" {...register('notes')} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={formState.isSubmitting}>
            Crear cliente
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface EditValues {
  licenseStatus: LicenseStatus;
  serviceUntil: string;
  modules: ModuleKey[];
  contactPhone: string;
  notes: string;
  country: string;
  province: string;
  locality: string;
  sellerId: string;
}

function EditTenantDialog({
  tenant,
  onClose,
  onSecret,
}: {
  tenant: TenantRowDTO;
  onClose: () => void;
  onSecret: (email: string, pw: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const manage = can('tenants.manage');
  const [archiving, setArchiving] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const archive = useMutation({
    mutationFn: () => api.post(`/platform/billing/tenants/${tenant.id}/archive`, { confirmName }),
    onSuccess: async () => {
      toast.success('Cliente archivado', tenant.name);
      await qc.invalidateQueries({ queryKey: ['tenants'] });
      onClose();
    },
    onError: (err) => toast.error('No se pudo archivar', errorMessage(err)),
  });
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, control, formState } = useForm<EditValues>({
    defaultValues: {
      licenseStatus: tenant.licenseStatus,
      serviceUntil: tenant.serviceUntil ?? '',
      modules: tenant.modules,
      contactPhone: tenant.contactPhone ?? '',
      notes: tenant.notes ?? '',
      country: tenant.country ?? 'AR',
      province: tenant.province ?? '',
      locality: tenant.locality ?? '',
      sellerId: tenant.sellerId ?? '',
    },
  });
  const sellers = useQuery({ queryKey: ['sellers'], queryFn: () => api.get<SellerDTO[]>('/platform/sellers'), enabled: can('sellers.view') });
  const reset = useMutation({
    mutationFn: () => api.post<{ email: string; temporaryPassword: string }>(`/platform/tenants/${tenant.id}/reset-owner-password`),
    onSuccess: (r) => {
      onClose();
      onSecret(r.email, r.temporaryPassword);
    },
    onError: (err) => toast.error('No se pudo generar la contraseña', errorMessage(err)),
  });

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    try {
      await api.patch(`/platform/tenants/${tenant.id}`, {
        ...v,
        serviceUntil: v.serviceUntil || null,
        sellerId: can('sellers.view') ? v.sellerId || null : undefined,
      });
      toast.success('Cliente actualizado', tenant.name);
      await qc.invalidateQueries({ queryKey: ['tenants'] });
      onClose();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => !o && onClose()}
      title={tenant.name}
      description={`${tenant.branches} sucursales, ${tenant.users} usuarios. Alta el ${formatDate(tenant.createdAt)}.`}
    >
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <div className="bf-form-row">
          <Field label="Plan" hint="El plan no se cambia. Para crecer, prendé adicionales abajo.">
            <Input value={planDisplayName(tenant.plan, tenant.modules)} readOnly />
          </Field>
          <Field label="Licencia" hint="Solo lectura: puede consultar pero no operar. Suspendida: no puede entrar.">
            <Select {...register('licenseStatus')}>
              {LICENSE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {LICENSE_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Abono pago hasta">
            <Input type="date" {...register('serviceUntil')} />
          </Field>
        </div>
        <div className="bf-field">
          <span className="bf-label">Módulos</span>
          <Controller
            control={control}
            name="modules"
            render={({ field }) => <ModuleSwitches plan={tenant.plan} value={field.value} onChange={field.onChange} />}
          />
        </div>
        <div className="bf-form-row">
          <Field label="Teléfono de contacto">
            <Input {...register('contactPhone')} />
          </Field>
          <Field label="País">
            <Select {...register('country')}>
              {COUNTRIES.map(([c, n]) => (
                <option key={c} value={c}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Provincia">
            <Select {...register('province')}>
              <option value="">—</option>
              {PROVINCES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              <option value="Fuera de Argentina">Fuera de Argentina</option>
            </Select>
          </Field>
          <Field label="Localidad">
            <Input {...register('locality')} maxLength={80} />
          </Field>
        </div>
        {can('sellers.view') && (
          <Field label="Vendedor o partner" hint="Quién trajo al cliente: cobra las renovaciones y ampliaciones si se las habilitaste.">
            <Select {...register('sellerId')}>
              <option value="">Sin vendedor</option>
              {(sellers.data ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                  {x.isActive ? '' : ' (de baja)'}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Notas internas">
          <Textarea {...register('notes')} />
        </Field>
        {can('tenants.archive') &&
          (archiving ? (
            <Callout tone="danger">
              <strong>Archivar {tenant.name}.</strong> Deja de aparecer, nadie puede entrar y se cancela su mantenimiento en Stripe. Los datos quedan
              guardados. Escribí el nombre del cliente para confirmar:
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={tenant.name} style={{ maxWidth: 280 }} />
                <Button
                  variant="danger"
                  loading={archive.isPending}
                  disabled={confirmName.trim().toLowerCase() !== tenant.name.trim().toLowerCase()}
                  onClick={() => archive.mutate()}
                >
                  Archivar cliente
                </Button>
                <Button onClick={() => setArchiving(false)}>Cancelar</Button>
              </div>
            </Callout>
          ) : null)}
        {manage && <DemoDataBlock tenant={tenant} />}
        <div className="bf-form-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {manage && (
              <Button icon={<KeyRound />} variant="ghost" onClick={() => reset.mutate()} loading={reset.isPending}>
                Nueva contraseña para el dueño
              </Button>
            )}
            {can('tenants.archive') && !archiving && (
              <Button icon={<Archive />} variant="ghost" onClick={() => setArchiving(true)}>
                Archivar cliente
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={onClose}>{manage ? 'Cancelar' : 'Cerrar'}</Button>
            {manage && (
              <Button type="submit" variant="primary" loading={formState.isSubmitting}>
                Guardar cambios
              </Button>
            )}
          </div>
        </div>
      </form>
    </Dialog>
  );
}

type DemoJob = { status: 'RUNNING' | 'DONE' | 'ERROR'; step: string; error: string | null; summary: string[] };

/** Cargar datos de demostración: una carnicería completa con 30 días de historia, para mostrarle el sistema a un cliente. */
function DemoDataBlock({ tenant }: { tenant: TenantRowDTO }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const status = useQuery({
    queryKey: ['demo-data', tenant.id],
    queryFn: () => api.get<{ job: DemoJob | null }>(`/platform/tenants/${tenant.id}/demo-data`),
    refetchInterval: (q) => (q.state.data?.job?.status === 'RUNNING' ? 1500 : false),
  });
  const start = useMutation({
    mutationFn: () => api.post<{ job: DemoJob }>(`/platform/tenants/${tenant.id}/demo-data`, { confirmName: name }),
    onSuccess: () => void status.refetch(),
    onError: (err) => toast.error('No se pudieron cargar los datos', errorMessage(err)),
  });
  const job = status.data?.job ?? null;
  if (job?.status === 'RUNNING')
    return (
      <Callout tone="info">
        <strong>Cargando datos de demostración…</strong> {job.step}. Tarda un par de minutos; podés cerrar esta ventana.
      </Callout>
    );
  if (job?.status === 'DONE')
    return (
      <Callout tone="success">
        <strong>Datos de demostración cargados.</strong> {job.summary.join('. ')}.
      </Callout>
    );
  return (
    <>
      {job?.status === 'ERROR' && <Callout tone="danger">No se terminaron de cargar los datos de demostración: {job.error}</Callout>}
      {open ? (
        <Callout tone="warning">
          <strong>Cargar datos de demostración en {tenant.name}.</strong> Suma una carnicería completa con 30 días de historia: productos, compras,
          ventas y caja de cada día, fiado, mermas, empleados y, si tiene Mayorista, clientes, pedidos, cobros y la hoja de ruta de hoy. Es solo para
          cuentas de prueba: no se puede deshacer y no se carga si la cuenta ya tiene ventas. Escribí el nombre del cliente para confirmar:
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={tenant.name} style={{ maxWidth: 280 }} />
            <Button
              variant="primary"
              loading={start.isPending}
              disabled={name.trim().toLowerCase() !== tenant.name.trim().toLowerCase()}
              onClick={() => start.mutate()}
            >
              Cargar datos
            </Button>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </Callout>
      ) : (
        <div>
          <Button icon={<Database />} variant="ghost" onClick={() => setOpen(true)}>
            Cargar datos de demostración
          </Button>
        </div>
      )}
    </>
  );
}

function SecretDialog({ title, email, password, onClose }: { title: string; email: string; password: string; onClose: () => void }) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Ingresá en https://app.bifeai.com\nUsuario: ${email}\nContraseña temporal: ${password}`);
      toast.success('Copiado', 'Listo para mandarle al cliente.');
    } catch {
      toast.error('No se pudo copiar', 'Copialo a mano antes de cerrar.');
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={title}
      description="La contraseña se muestra una sola vez. En el primer ingreso tiene que activar la verificación en dos pasos y elegir su propia contraseña."
    >
      <div className="bf-form">
        <div className="bf-field">
          <span className="bf-label">Usuario</span>
          <p className="bf-secret" style={{ letterSpacing: 0 }}>
            {email}
          </p>
        </div>
        <div className="bf-field">
          <span className="bf-label">Contraseña temporal</span>
          <p className="bf-secret" style={{ fontSize: 'var(--bf-text-xl)' }}>
            {password}
          </p>
        </div>
        <div className="bf-form-actions">
          <Button icon={<Copy />} onClick={() => void copy()}>
            Copiar datos
          </Button>
          <Button variant="primary" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
